import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, ChevronDown, Calendar } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { usePeriod } from "@/lib/period-context";

const PERIODS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
];

function titleFromPath(pathname) {
  if (pathname.startsWith("/clients/")) return ["Detalle de cliente", "Métricas e integraciones por cliente"];
  if (pathname.startsWith("/clients")) return ["Clientes", "Gestioná y revisá tu portafolio"];
  if (pathname.startsWith("/integrations")) return ["Integraciones", "Conectá fuentes de datos por cliente"];
  if (pathname.startsWith("/alerts")) return ["Alertas", "Cambios significativos en métricas"];
  if (pathname.startsWith("/users")) return ["Usuarios", "Gestión de accesos y roles"];
  return ["Narwhal", ""];
}

export default function Header() {
  const location = useLocation();
  const [title, subtitle] = titleFromPath(location.pathname);
  const { period, setPeriod } = usePeriod();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-20 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200"
    >
      <div className="h-full px-6 md:px-8 flex items-center justify-between gap-4 max-w-[1600px] mx-auto w-full">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
            <Link to="/dashboard" className="hover:text-slate-900">Narwhal</Link>
            <span className="text-slate-300">/</span>
            <span className="text-slate-700">{title}</span>
          </div>
          <h1 className="font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight leading-tight">
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-xs text-slate-500 font-mono">
            <Calendar className="h-3.5 w-3.5" />
            {now.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="date-range-picker"
                className="h-9 rounded-md border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-medium"
              >
                {PERIODS.find((p) => p.value === period)?.label || "Last 30 days"}
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Comparison period
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PERIODS.map((p) => (
                <DropdownMenuItem
                  key={p.value}
                  data-testid={`period-option-${p.value}`}
                  onSelect={() => setPeriod(p.value)}
                  className="text-sm"
                >
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="default"
            data-testid="header-search-btn"
            className="h-9 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
          >
            <Search className="h-4 w-4 mr-1.5" /> Quick find
          </Button>
        </div>
      </div>
    </header>
  );
}
